import { useEffect, useState } from "react";
import { Sparkles, Zap, Eye, Activity } from "lucide-react";
import { PulseAIImage, usePulseAIActive, PULSEAI_METADATA } from "../PulseAIImage";

// Compact "Powered by PulseAI Engine™" badge — goes above H1
export const PulseAIEngineBadge = ({ className = "" }) => {
  const brand = usePulseAIActive();
  const displayName = `${brand.name || "PulseAI"}${brand.suffix || " Engine™"}`;
  return (
    <div
      className={`inline-flex items-center gap-2.5 pl-1 pr-4 py-1 mb-6 bg-gradient-to-r from-purple-900/40 via-indigo-900/40 to-cyan-900/40 border border-purple-500/40 rounded-full backdrop-blur-sm ${className}`}
      data-testid="pulseai-badge"
    >
      <div className="shrink-0 ring-1 ring-cyan-400/50 rounded-full overflow-hidden">
        <PulseAIImage variantKey={brand.key} customUrl={brand.imageUrl} size={34} rounded />
      </div>
      <span className="text-[11px] font-['JetBrains_Mono'] uppercase tracking-widest text-cyan-300">
        Powered by{" "}
        <span className="text-white font-bold">{displayName}</span>
      </span>
    </div>
  );
};

// Large showcase section — "Meet PulseAI Engine™"
export const PulseAIEngineShowcase = ({ t = {} }) => {
  const brand = usePulseAIActive();
  const meta = PULSEAI_METADATA[brand.key];
  const displayName = `${brand.name || "PulseAI"}${brand.suffix || " Engine™"}`;
  const [scanRate, setScanRate] = useState(847);

  useEffect(() => {
    const id = setInterval(() => {
      setScanRate((prev) => {
        const delta = Math.floor(Math.random() * 20) - 10;
        return Math.max(800, Math.min(920, prev + delta));
      });
    }, 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="pulseai-engine"
      className="relative py-24 overflow-hidden themed-section"
      data-testid="pulseai-showcase"
      style={{ background: "linear-gradient(180deg, var(--color-bg) 0%, var(--color-surface-alt) 50%, var(--color-bg) 100%)" }}
    >
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#a855f7 1px, transparent 1px), linear-gradient(90deg, #a855f7 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left — Large cinematic image */}
        <div className="flex items-center justify-center">
          <div className="relative">
            <div className="absolute -inset-8 bg-gradient-to-br from-purple-500/30 to-cyan-500/30 rounded-full blur-3xl" />
            <div className="relative w-[340px] h-[340px] sm:w-[440px] sm:h-[440px] rounded-full bg-black ring-2 ring-purple-500/30 shadow-[0_0_80px_rgba(168,85,247,0.4)] overflow-hidden">
              <PulseAIImage
                variantKey={brand.key}
                customUrl={brand.imageUrl}
                size={440}
                rounded={false}
                className="w-full h-full"
              />
            </div>
            {/* Floating stats */}
            <div className="hidden lg:block absolute -left-16 top-12 px-3 py-2 bg-[var(--color-bg)]/80 border border-cyan-500/30 rounded-lg backdrop-blur">
              <p className="text-[10px] text-cyan-400 uppercase font-mono">Status</p>
              <p className="text-sm font-bold text-white flex items-center gap-1.5">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                Online
              </p>
            </div>
            <div className="hidden lg:block absolute -right-16 top-24 px-3 py-2 bg-[var(--color-bg)]/80 border border-purple-500/30 rounded-lg backdrop-blur">
              <p className="text-[10px] text-purple-300 uppercase font-mono">Version</p>
              <p className="text-sm font-bold text-white">v4.2</p>
            </div>
            <div className="hidden lg:block absolute -left-8 -bottom-4 px-3 py-2 bg-[var(--color-bg)]/80 border border-cyan-500/30 rounded-lg backdrop-blur">
              <p className="text-[10px] text-cyan-400 uppercase font-mono">Analýz / sek.</p>
              <p className="text-lg font-black font-mono text-white">{scanRate}</p>
            </div>
            {(brand.isCustom || meta) && (
              <div className="hidden lg:block absolute -right-8 -bottom-4 px-3 py-2 bg-[var(--color-bg)]/80 border border-fuchsia-500/30 rounded-lg backdrop-blur">
                <p className="text-[10px] text-fuchsia-300 uppercase font-mono">Persona</p>
                <p className="text-sm font-bold text-white">
                  {brand.isCustom ? (brand.name || "Custom") : meta.label.split(" ")[0]}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right — Copy */}
        <div>
          <p className="text-cyan-400 font-['JetBrains_Mono'] text-xs tracking-[0.3em] uppercase mb-4">
            {t.pulseai_tag || "AI Infrastructure"}
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-6 text-white">
            {t.pulseai_h1_a || "Meet"}{" "}
            <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
              {displayName}
            </span>
          </h2>
          <p className="text-[var(--color-text-muted)] text-lg mb-8 leading-relaxed">
            {t.pulseai_desc ||
              "Naša proprietárna AI infraštruktúra trénovaná na 2,4 miliardách SEO signálov, 180 000 AI dotazov a real-time dátach zo 47 vyhľadávačov — Google, Bing, ChatGPT, Perplexity, Claude, Gemini. O 73 % presnejšie odporúčania než generický GPT-4."}
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-4 p-4 bg-[var(--color-surface)]/60 border border-purple-500/20 rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-bold text-white mb-1">
                  {t.pulseai_p1_title || "Neural SEO Index"}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t.pulseai_p1_desc || "2,4 miliardy signálov zo SERP, aktualizované každých 4 hodín"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-[var(--color-surface)]/60 border border-cyan-500/20 rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center shrink-0">
                <Eye className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-bold text-white mb-1">
                  {t.pulseai_p2_title || "GEO Visibility Layer™"}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t.pulseai_p2_desc || "Live tracking cez 47 AI platforiem vrátane ChatGPT, Perplexity a Claude"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-[var(--color-surface)]/60 border border-fuchsia-500/20 rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-fuchsia-500/20 flex items-center justify-center shrink-0">
                <Activity className="w-5 h-5 text-fuchsia-400" />
              </div>
              <div>
                <h3 className="font-bold text-white mb-1">
                  {t.pulseai_p3_title || "Real-time Auto-Action"}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t.pulseai_p3_desc || "AI sama navrhne a spustí optimalizácie — kým ty spíš"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-900/40 to-cyan-900/40 border border-purple-500/30 rounded-lg">
            <Sparkles className="w-5 h-5 text-cyan-300 shrink-0" />
            <p className="text-sm text-cyan-100">
              <span className="font-bold text-white">73 % presnejšie</span>{" "}
              {t.pulseai_vs || "než čistý GPT-4 — overené na 12 500+ doménach"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Small marker used inside AI outputs
export const PulseAIGeneratedMark = ({ className = "" }) => {
  const brand = usePulseAIActive();
  const displayName = `${brand.name || "PulseAI"}${brand.suffix || " Engine™"}`;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-purple-400 ${className}`}
      data-testid="pulseai-generated-mark"
    >
      <PulseAIImage variantKey={brand.key} customUrl={brand.imageUrl} size={16} rounded />
      Generated by {displayName}
    </span>
  );
};
